/**
 * DataTable — generic data table component used across all analysis screens.
 *
 * Features:
 * - Dual synchronized scrollbars (top + bottom) for wide tables
 * - Conditional cell coloring via header.classify() threshold functions
 * - Sticky reference column (e.g., date or buyer name)
 * - Numeric formatting via the Number component (percentages, rounding, scaling)
 *
 * Props:
 *   data      — array of row objects
 *   headers   — column definitions with { key, text, datatype, display, classify, decimals, factor }
 *   thresholds — optional threshold values displayed in a sub-header row
 *   reference — key of the sticky reference column (e.g., "Reference Date")
 */
import { useSelector } from "react-redux";
import { useRef, useEffect, useCallback } from "react";
// -------------------------------------------------------------
import Number from "Components/Number";

// Replaces regular spaces with non-breaking spaces to prevent unwanted text wrapping in table cells
const clean = (str) => {
  if (typeof str !== "string") return str;
  if (str === null) return null;
  return str.replace(/ /g, "\u00A0");
};

// Cell value styling by semantic type — the visual language of the table.
const numStyle = { fontVariantNumeric: "tabular-nums", fontWeight: 600 };
const chip = (bg, color) => ({
  display: "inline-block",
  padding: "1px 7px",
  borderRadius: "5px",
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  backgroundColor: bg,
  color,
});
const PCT_CHIP = chip("rgba(121, 88, 159, 0.12)", "var(--ev-purple-dark)");
const PCT_NEG_CHIP = chip("rgba(214, 69, 69, 0.13)", "var(--ev-danger)");
const PASS_CHIP = chip("rgba(61, 184, 135, 0.16)", "var(--ev-success)");
const FAIL_CHIP = chip("rgba(214, 69, 69, 0.16)", "var(--ev-danger)");

const DataTable = ({ data, headers, thresholds, reference }) => {
  // eslint-disable-next-line no-unused-vars
  const { buyerView } = useSelector((state) => state.project);
  const preFactor = 1; //buyerView ? 1 : -1;

  // Render one value cell, styled by whether it's a percentage, a pass/fail
  // classified value, text, or a raw number (with sign-aware coloring).
  const renderValue = (header, row) => {
    const raw = row[header.key];

    if (header.datatype === "string") {
      return <span style={{ color: "var(--ev-text)" }}>{clean(raw)}</span>;
    }

    const num = parseFloat(raw);
    const isPct = header.display === "percentage";
    const formatted = (
      <Number
        column={header}
        value={clean(raw * preFactor)}
        isPercentage={isPct}
        isRounded={header.isRounded}
        factor={header.factor}
      />
    );

    // Pass/fail classification wins (criteria / advance screens).
    if (header.classify) {
      return <span style={header.classify(thresholds, row) ? PASS_CHIP : FAIL_CHIP}>{formatted}</span>;
    }
    // Percentages read as accent chips — instantly distinct from raw magnitudes.
    if (isPct) {
      return <span style={num < 0 ? PCT_NEG_CHIP : PCT_CHIP}>{formatted}</span>;
    }
    // Raw numbers: clean tabular figures; red negatives, muted zeros/blanks.
    const color =
      raw === "" || raw === null || raw === undefined || num === 0
        ? "var(--ev-text-muted)"
        : num < 0
        ? "var(--ev-danger)"
        : "var(--ev-text)";
    return <span style={{ ...numStyle, color }}>{formatted}</span>;
  };

  const topScrollRef = useRef(null);
  const tableContainerRef = useRef(null);
  const syncing = useRef(false);

  // Syncs horizontal scroll position between two elements.
  // Uses a syncing guard to prevent infinite scroll event loops.
  const syncScroll = useCallback((source, target) => {
    if (syncing.current) return;
    syncing.current = true;
    target.scrollLeft = source.scrollLeft;
    syncing.current = false;
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const bottom = tableContainerRef.current;
    if (!top || !bottom) return;

    const onTopScroll = () => syncScroll(top, bottom);
    const onBottomScroll = () => syncScroll(bottom, top);

    top.addEventListener("scroll", onTopScroll);
    bottom.addEventListener("scroll", onBottomScroll);

    const observer = new ResizeObserver(() => {
      const inner = top.firstChild;
      if (inner) inner.style.width = bottom.scrollWidth + "px";
    });
    observer.observe(bottom);

    return () => {
      top.removeEventListener("scroll", onTopScroll);
      bottom.removeEventListener("scroll", onBottomScroll);
      observer.disconnect();
    };
  }, [syncScroll, data]);

  return (
    <div>
      <div ref={topScrollRef} style={{ overflowX: "auto", overflowY: "hidden", height: "12px" }}>
        <div style={{ height: "1px" }} />
      </div>
      <div className="table-container" ref={tableContainerRef} style={{ paddingRight: "1rem" }}>
      <table className="table is-fullwidth is-striped is-bordered is-small-table">
        <thead>
          <tr className="header-row">
            {headers.map((h) => (
              <th key={h.key} className="is-light">
                <div className="tags has-addons">
                  <span className="tag is-warning is-light">{h.text}</span>
                </div>
              </th>
            ))}
          </tr>
          {thresholds && (
            <tr style={{ backgroundColor: "var(--ev-surface)" }}>
              {headers.map((header) => {
                if (!thresholds[header.text])
                  return <th key = {header.key}></th>;
                return (
                  <th key={header.key}>
                    <div style={{ textAlign: "center" }}>
                      <span className="tag is-primary is-light">
                        {thresholds[header.text].unit === "%"
                          ? `${thresholds[header.text].value}${
                              thresholds[header.text].unit
                            }`
                          : `${thresholds[header.text].value} ${
                              thresholds[header.text].unit
                            }`}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          )}
        </thead>
        <tbody>
          {data.map((row, rowindex) => (
            <tr
              key={`row-${rowindex}`}
              className="numbers-row"
              style={{
                animation: "ev-fade-in 0.3s ease both",
                // Cascade the first ~20 rows; the rest reveal together.
                animationDelay: `${Math.min(rowindex, 20) * 18}ms`,
              }}
            >
              {headers.map((header) => {
                if (header.key === reference)
                  return (
                    <th
                      className="is-light sticky-column ev-ref-cell"
                      key={`${rowindex}-${header.key}`}
                      style={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        whiteSpace: "nowrap",
                        color: "var(--ev-purple-dark)",
                        backgroundColor: "var(--ev-purple-ghost)",
                        boxShadow: "inset 3px 0 0 var(--ev-purple-light)",
                      }}
                    >
                      {clean(row[header.key])}
                    </th>
                  );
                return (
                  <td key={`${rowindex}-${header.key}`}>
                    {renderValue(header, row)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default DataTable;
